import React, { useCallback } from "react";
import { Link } from "@tanstack/react-router";
import { useI18N, useFooterSettings } from "common/hooks";
import { trackEvent } from "common/analytics";
import footerDataFallback from "./footerData.json";
import { FacebookLogoIcon, InstagramLogoIcon, LinkedinLogoIcon } from "@phosphor-icons/react";
import { useDarkMode } from "@/hooks/useDarkMode";

interface FooterProps {
  className?: string;
}

const placesLinkClass =
  "focus-blue inline-flex min-h-[40px] items-center justify-center rounded-lg border border-border-token-brand-minimal bg-bg-brand-secondary-default px-[10px] label-m-medium text-fg-neutral-strong transition-all duration-200 hover:bg-bg-brand-secondary-hover active:bg-bg-brand-secondary-active";

export const Footer: React.FC<FooterProps> = ({ className = "" }) => {
  const i18n = useI18N();
  const isDark = useDarkMode();
  const { footerSettings, isLoading } = useFooterSettings();

  const footerData = footerSettings || footerDataFallback;

  const handleAppStoreClick = useCallback(
    (_platform: string) => {},
    [],
  );

  const handleCheckOutMoreClick = useCallback(
    (linkId: string, _url: string) => {
      // community_subscribe_cta_activated — the "Sign up to our newsletter" footer CTA. Footer is a
      // shared widget (renders on every page), so this is the primitive trackEvent, not a page-scoped
      // createPageAnalytics. Matched on the stable link id, not the i18n label.
      if (linkId === "newsletter") {
        trackEvent("community_subscribe_cta_activated", {});
      }
    },
    [],
  );

  const handleEmailClick = useCallback(() => {}, []);

  const handleSocialMediaClick = useCallback(
    (_platform: string, _url: string) => {},
    [],
  );

  if (isLoading) {
    return (
      <footer
        className={`w-full pb-safe ${className}`}
        role="contentinfo"
        aria-busy="true"
        aria-label="Footer loading"
      >
        <div className="rounded-lg bg-surface-tertiary px-4 py-10 md:px-6 lg:px-8">
          <div className="mx-auto max-w-[1280px]">
            <div className="flex animate-pulse flex-col gap-8 lg:flex-row">
              <div className="flex flex-col gap-4 lg:flex-1">
                <div className="h-[150px] w-[150px] rounded-lg bg-container-neutral-minimal" />
                <div className="h-8 w-48 rounded bg-container-neutral-minimal" />
              </div>
              <div className="flex flex-col gap-4 lg:flex-1">
                <div className="h-6 w-40 rounded bg-container-neutral-minimal" />
                <div className="h-20 w-full rounded bg-container-neutral-minimal" />
              </div>
            </div>
            {/* Server-rendered while the settings load, so crawlers always see the link. */}
            <div className="mt-6">
              <Link to="/places" activeOptions={{ exact: true }} className={placesLinkClass}>
                {i18n.get("footerPlacesLink")}
              </Link>
            </div>
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className={`w-full pb-safe ${className}`} role="contentinfo">
      <div className="rounded-lg bg-surface-tertiary px-4 py-10 md:px-6 md:py-4 lg:px-8 lg:py-10">
        <div className="mx-auto max-w-[1280px]">
          {/*
           * Outer layout:
           *   Mobile/Tablet â†’ single column (flex-col)
           *   Desktop (lg:) â†’ two columns side-by-side (flex-row)
           */}
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:gap-4">

            {/* â”€â”€ LEFT COLUMN: QR Â· Stats Â· App downloads â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
            <section
              aria-labelledby="footer-download-heading"
              className="flex flex-col gap-4 lg:flex-1"
            >
              <h2 id="footer-download-heading" className="sr-only">
                Download Our App
              </h2>

              {/*
               * QR code + Stats row
               * Mobile:  QR (w-1/2) | Stats stacked vertically (w-1/2)
               * Tablet+: QR fixed   | Stats in a horizontal row
               */}
              <div className="flex flex-row items-stretch gap-4">
                {/* QR Code */}
                <div className="shrink-0 flex flex-col">
                  <img
                    src="/images/Sociability_App_Download_Smart_Link_QR_Code.png"
                    alt="QR Code to download Sociability app"
                    className="h-full w-auto max-w-[calc(50vw-2rem)] rounded-lg object-contain md:max-w-[150px]"
                  />
                </div>

                {/* Stats + (on tablet+) app downloads  */}
                <div className="flex flex-1 flex-col gap-4">

                  {/*
                   * Stats list
                   * Mobile:  vertical stack, horizontal rule between items
                   * Tablet+: horizontal row, vertical pipe between items
                   */}
                  <div className="flex flex-col gap-1 md:flex-row md:items-center md:gap-4">
                    {footerData.stats.map((stat, index) => (
                      <React.Fragment key={stat.id}>
                        <div className="flex flex-col gap-0.5">
                          <span className="headline-m-semibold text-fg-neutral-strong">
                            {stat.value}
                          </span>
                          <span className="body-m-medium text-fg-neutral-subtle">
                            {i18n.t(stat.labelKey)}
                          </span>
                        </div>

                        {index < footerData.stats.length - 1 && (
                          <>
                            {/* Mobile: horizontal rule */}
                            <div
                              className="h-px w-full bg-border-token-brand-minimal md:hidden"
                              aria-hidden="true"
                            />
                            {/* Tablet+: vertical pipe */}
                            <div
                              className="hidden h-12 w-px shrink-0 bg-border-token-brand-minimal md:block"
                              aria-hidden="true"
                            />
                          </>
                        )}
                      </React.Fragment>
                    ))}
                  </div>

                  {/* App store buttons â€” visible on tablet+ inside the stats column */}
                  <div className="hidden md:flex md:flex-wrap md:items-center md:gap-3">
                    {footerData.appStoreLinks.map((link) => (
                      <a
                        key={link.id}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => handleAppStoreClick(link.platform)}
                        className="focus-blue inline-flex h-10 w-[120px] shrink-0 items-center justify-center transition-opacity hover:opacity-80"
                        aria-label={`${i18n.t(link.ariaLabelKey)}, ${i18n.t("openInNewTab")}`}
                      >
                        <img
                          src={link.image}
                          alt=""
                          className="h-full w-full object-contain"
                        />
                      </a>
                    ))}
                  </div>

                  {/* Scan instruction â€” visible on tablet+ */}
                  <p className="hidden body-m-medium text-fg-neutral-subtle md:block">
                    {i18n.t("scanTheQRCodeToDownloadTheApp")}
                  </p>
                </div>
              </div>

              {/* App store buttons â€” mobile only (below the QR/stats row) */}
              <div className="flex flex-wrap items-center gap-3 md:hidden">
                {footerData.appStoreLinks.map((link) => (
                  <a
                    key={link.id}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => handleAppStoreClick(link.platform)}
                    className="focus-blue inline-flex h-10 w-[120px] shrink-0 items-center justify-center transition-opacity hover:opacity-80"
                    aria-label={`${i18n.t(link.ariaLabelKey)}, ${i18n.t("openInNewTab")}`}
                  >
                    <img
                      src={link.image}
                      alt=""
                      className="h-full w-full object-contain"
                    />
                  </a>
                ))}
              </div>

              {/* Scan instruction â€” mobile only */}
              <p className="body-m-medium text-fg-neutral-subtle md:hidden">
                {i18n.t("scanTheQRCodeToDownloadTheApp")}
              </p>
            </section>

            {/* â”€â”€ RIGHT COLUMN: CheckIcon out more Â· Connect with us â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
            <div className="flex flex-col gap-6 lg:flex-1 lg:px-3">

              {/* CheckIcon out more */}
              <nav
                aria-labelledby="footer-links-heading"
                className="flex flex-col gap-2"
              >
                <h2
                  id="footer-links-heading"
                  className="body-l-semibold text-fg-neutral-strong"
                >
                  {i18n.t("checkOutMore")}
                </h2>

                <div className="flex flex-wrap gap-2">
                  <Link to="/places" activeOptions={{ exact: true }} className={placesLinkClass}>
                    {i18n.get("footerPlacesLink")}
                  </Link>
                  {footerData.checkOutMoreLinks.map((link) => (
                    <a
                      key={link.id}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() =>
                        handleCheckOutMoreClick(link.id, link.url)
                      }
                      className="focus-blue inline-flex min-h-[40px] items-center justify-center rounded-lg border border-border-token-brand-minimal bg-bg-brand-secondary-default px-[10px] label-m-medium text-fg-neutral-strong transition-all duration-200 hover:bg-bg-brand-secondary-hover active:bg-bg-brand-secondary-active"
                    >
                      {i18n.t(link.labelKey)}
                      <span className="sr-only">, {i18n.t("openInNewTab")}</span>
                    </a>
                  ))}
                </div>
              </nav>

              {/* Connect with us */}
              <section
                aria-labelledby="footer-connect-heading"
                className="flex flex-col gap-2"
              >
                <h2
                  id="footer-connect-heading"
                  className="body-l-semibold text-fg-neutral-strong"
                >
                  {i18n.t("connectWithUs")}
                </h2>

                <p className="body-m-medium text-fg-neutral-subtle">
                  {i18n.t("letUsKnowHowWeCanImprove")}{" "}
                  <a
                    href={`mailto:${footerData.email}`}
                    onClick={handleEmailClick}
                    className="focus-blue body-m-medium text-fg-brand-subtle underline decoration-solid hover:text-fg-brand-strong"
                  >
                    {footerData.email}
                  </a>
                </p>

                {/* Social media icon buttons */}
                <div className="flex flex-wrap gap-2">
                  {footerData.socialMediaLinks.map((social) => (
                    <a
                      key={social.id}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() =>
                        handleSocialMediaClick(social.id, social.url)
                      }
                      className="focus-blue inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-border-token-brand-minimal bg-bg-brand-secondary-default text-fg-neutral-strong transition-all duration-200 hover:bg-bg-brand-secondary-hover active:bg-bg-brand-secondary-active"
                      aria-label={`${i18n.t(social.ariaLabelKey)}, ${i18n.t("openInNewTab")}`}
                    >
                      {social.id === "instagram" && (
                        <InstagramLogoIcon size={20} aria-hidden="true" />
                      )}
                      {social.id === "facebook" && (
                        <FacebookLogoIcon size={20} aria-hidden="true" />
                      )}
                      {social.id === "tiktok" && (
                        <img
                          src={
                            isDark
                              ? "/images/ThreadsLogo_dark.svg"
                              : "/images/ThreadsLogo.svg"
                          }
                          alt=""
                          aria-hidden="true"
                          className="h-5 w-5 object-contain"
                        />
                      )}
                      {social.id === "linkedin" && (
                        <LinkedinLogoIcon size={20} aria-hidden="true" />
                      )}
                    </a>
                  ))}
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
